import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  MoreHorizontal,
  Palette,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TAG_COLOR_KEYS, getTagColor } from "@/lib/constants";
import { TagSwatch } from "./TagBadge";
import {
  createTag,
  deleteTag,
  listTags,
  updateTag,
} from "@/api/tags";
import { usePermissions } from "@/hooks/usePermissions";
import type { TagWithCount } from "@/api/types";

interface TagManagerProps {
  projectSlug: string;
}

export function TagManager({ projectSlug }: TagManagerProps) {
  const canEdit = usePermissions().canManageTags;
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [renameTargetId, setRenameTargetId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [colorPickerForId, setColorPickerForId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TagWithCount | null>(null);
  const [busy, setBusy] = useState<{ id: number; kind: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await listTags(projectSlug);
      setTags(res.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setErrorMessage(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  function applyTagUpdate(id: number, patch: Partial<TagWithCount>) {
    setTags((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    );
  }

  async function commitRename(id: number) {
    const name = renameValue.trim();
    setRenameTargetId(null);
    if (!name) return;
    const previous = tags.find((t) => t.id === id);
    if (!previous || previous.name === name) return;
    applyTagUpdate(id, { name });
    setBusy({ id, kind: "rename" });
    try {
      const updated = await updateTag(projectSlug, id, { name });
      applyTagUpdate(id, updated);
    } catch (err) {
      applyTagUpdate(id, { name: previous.name });
      toast.error(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function commitColor(id: number, color: string) {
    setColorPickerForId(null);
    const previous = tags.find((t) => t.id === id);
    if (!previous || previous.color === color) return;
    applyTagUpdate(id, { color });
    setBusy({ id, kind: "color" });
    try {
      const updated = await updateTag(projectSlug, id, { color });
      applyTagUpdate(id, updated);
    } catch (err) {
      applyTagUpdate(id, { color: previous.color });
      toast.error(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    const target = confirmDelete;
    setBusy({ id: target.id, kind: "delete" });
    try {
      await deleteTag(projectSlug, target.id);
      setTags((prev) => prev.filter((t) => t.id !== target.id));
      setConfirmDelete(null);
      toast.success(`Tag "${target.name}" deleted`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setBusy(null);
    }
  }

  function handleCreated(tag: TagWithCount) {
    setTags((prev) =>
      [...prev, tag].sort((a, b) => a.name.localeCompare(b.name)),
    );
  }

  return (
    <section className="space-y-5">
      <header className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-slate-900">
            Tags
          </h2>
          <p className="text-sm text-slate-500">
            {tags.length} {tags.length === 1 ? "tag" : "tags"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
          >
            <RefreshCcw
              className={
                loading
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              New tag
            </Button>
          )}
        </div>
      </header>

      <div className="rounded-plane bg-white shadow-plane">
        {loading ? (
          <div className="flex items-center justify-center px-5 py-10 text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading tags…
          </div>
        ) : errorMessage ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-rose-700">
              Couldn&rsquo;t load tags
            </p>
            <p className="mt-1 text-xs text-rose-600">{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        ) : tags.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            No tags yet. Create one to start labeling companies.
          </div>
        ) : (
          <ul className="divide-y divide-slate-900/[0.06]">
            {tags.map((t) => {
              const colors = getTagColor(t.color);
              const isRenaming = renameTargetId === t.id;
              const isPickingColor = colorPickerForId === t.id;
              return (
                <li
                  key={t.id}
                  className="flex items-center gap-4 px-5 py-3"
                >
                  <Popover
                    open={isPickingColor}
                    onOpenChange={(o) => {
                      if (!o) setColorPickerForId(null);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        onClick={() => {
                          if (canEdit) setColorPickerForId(t.id);
                        }}
                        disabled={!canEdit}
                        className={cn(
                          "h-4 w-4 shrink-0 rounded-full",
                          colors.swatch,
                          canEdit
                            ? "transition-transform hover:scale-110"
                            : "cursor-default",
                        )}
                        aria-label={`Change color of ${t.name}`}
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={6}
                      className="w-auto p-3"
                    >
                      <div className="grid grid-cols-9 gap-1.5">
                        {TAG_COLOR_KEYS.map((c) => (
                          <TagSwatch
                            key={c}
                            color={c}
                            selected={c === t.color}
                            onClick={() => void commitColor(t.id, c)}
                          />
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={renameValue}
                          autoFocus
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void commitRename(t.id);
                            if (e.key === "Escape") setRenameTargetId(null);
                          }}
                          className="h-8 max-w-[260px] rounded-lg border-slate-200"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => void commitRename(t.id)}
                          className="h-8 w-8"
                          aria-label="Confirm rename"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setRenameTargetId(null)}
                          className="h-8 w-8"
                          aria-label="Cancel rename"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-slate-900">
                          {t.name}
                          {busy?.id === t.id && (
                            <Loader2 className="ml-2 inline h-3 w-3 animate-spin text-slate-400" />
                          )}
                        </div>
                        <div className="text-xs text-slate-400">
                          Used by {t.company_count}{" "}
                          {t.company_count === 1 ? "company" : "companies"}
                        </div>
                      </>
                    )}
                  </div>

                  {canEdit && !isRenaming && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-500"
                          aria-label={`Actions for ${t.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                          onSelect={() => {
                            setRenameValue(t.name);
                            setRenameTargetId(t.id);
                          }}
                        >
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => setColorPickerForId(t.id)}
                        >
                          <Palette className="mr-2 h-3.5 w-3.5" />
                          Change color
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                          onSelect={() => setConfirmDelete(t)}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CreateTagDialog
        projectSlug={projectSlug}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => {
          if (busy?.kind === "delete") return;
          if (!o) setConfirmDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete tag?</DialogTitle>
            <DialogDescription>
              {confirmDelete && (
                <>
                  This will remove the tag{" "}
                  <span className="font-medium text-slate-900">
                    &ldquo;{confirmDelete.name}&rdquo;
                  </span>
                  . It will be removed from{" "}
                  <span className="font-medium text-slate-900">
                    {confirmDelete.company_count}{" "}
                    {confirmDelete.company_count === 1
                      ? "company"
                      : "companies"}
                  </span>
                  .
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <p className="text-xs leading-relaxed text-rose-700">
              The tag will be permanently deleted. Companies keep their other
              tags.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={busy?.kind === "delete"}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={busy?.kind === "delete"}
            >
              {busy?.kind === "delete" && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ─── Create dialog ─────────────────────────────────────────────────────────

interface CreateTagDialogProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (tag: TagWithCount) => void;
}

function CreateTagDialog({
  projectSlug,
  open,
  onOpenChange,
  onCreated,
}: CreateTagDialogProps) {
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>("indigo");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setColor("indigo");
    }
  }, [open]);

  async function submit() {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const created = await createTag(projectSlug, {
        name: name.trim(),
        color,
      });
      onCreated({ ...created, company_count: 0 });
      toast.success(`Tag "${created.name}" created`);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>New tag</DialogTitle>
          <DialogDescription>
            Tags help you label and filter companies in this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="High intent"
              maxLength={50}
              className="h-10 rounded-lg border-slate-200"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {TAG_COLOR_KEYS.map((c) => (
                <TagSwatch
                  key={c}
                  color={c}
                  selected={c === color}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void submit()}
            disabled={submitting || name.trim().length === 0}
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 409) {
      return "A tag with this name already exists.";
    }
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return "Couldn't save the tag.";
}
